import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import LeaveMultiDatePicker from "../utils/leave/LeaveMultiDatePicker";
import {
	Button,
	Card,
	Form,
	Input,
	InputNumber,
	Modal,
	Progress,
	Select,
	Space,
	Table,
	Tooltip,
	Typography,
	message,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

dayjs.extend(isSameOrBefore);

const { Text } = Typography;
const { TextArea } = Input;

const LEAVE_TYPE_OPTIONS = [
	{ value: "연차", label: "연차" },
	{ value: "오전반차", label: "오전 반차" },
	{ value: "오후반차", label: "오후 반차" },
];

const formatDays = (value) => {
	const number = Number(value);
	if (!Number.isFinite(number)) {
		return "-";
	}
	const rounded = Math.round(number * 10) / 10;
	return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const disableSunday = (current) => Boolean(current && current.day() === 0);

const itemDayCount = (item) => {
	const count = (item?.dates || []).length;
	if (
		item?.leave_type === "오전반차" ||
		item?.leave_type === "오후반차"
	) {
		return count * 0.5;
	}
	return count;
};

const WEEKDAY_KO = ["일", "월", "화", "수", "목", "금", "토"];

const formatAsOfLabel = (isoDate) => {
	if (!isoDate) {
		return "-";
	}
	const date = dayjs(isoDate);
	return `${date.format("YYYY.MM.DD")}(${WEEKDAY_KO[date.day()]})`;
};

const formatShortDate = (isoDate) => {
	const date = dayjs(isoDate);
	return `${date.format("MM.DD")}(${WEEKDAY_KO[date.day()]})`;
};

const dayCountForPeriod = (leaveType, start, end) => {
	if (leaveType === "오전반차" || leaveType === "오후반차") {
		return 0.5;
	}
	return dayjs(end).diff(dayjs(start), "day") + 1;
};

const findConversion = (conversions, start, end) =>
	(conversions || []).find(
		(row) => row.start_date === start && row.end_date === end,
	);

const datesForPeriod = (item, period) => {
	const start = dayjs(period.start_date).startOf("day");
	const end = dayjs(period.end_date).startOf("day");
	return (item.dates || [])
		.filter((day) => {
			const parsed = dayjs(day).startOf("day");
			return (
				!parsed.isBefore(start, "day") &&
				!parsed.isAfter(end, "day")
			);
		})
		.map((day) => dayjs(day));
};

/** 수정 화면: 한 계획 행 안의 연속 날짜 구간마다 따로 편집할 수 있게 펼친다. */
const expandPlanItemsForForm = (items) => {
	const rows = [];
	(items || []).forEach((item) => {
		const periods = item.periods?.length
			? item.periods
			: (item.dates || []).map((day) => ({
					start_date: day,
					end_date: day,
					locked: false,
				}));
		let memoAssigned = false;
		periods.forEach((period) => {
			const conversion = findConversion(
				item.conversions,
				period.start_date,
				period.end_date,
			);
			const locked =
				period.locked ??
				(conversion?.status === "converted" &&
					conversion?.application_active);
			rows.push({
				id: item.id,
				leave_type: item.leave_type,
				dates: datesForPeriod(item, period),
				memo: memoAssigned ? "" : item.memo || "",
				locked,
			});
			memoAssigned = true;
		});
	});
	return rows;
};

const formatPeriodTitle = (dates) => {
	if (!dates?.length) {
		return "";
	}
	const sorted = [...dates].sort((a, b) =>
		dayjs(a).valueOf() - dayjs(b).valueOf(),
	);
	const start = sorted[0];
	const end = sorted[sorted.length - 1];
	if (dayjs(start).isSame(end, "day")) {
		return formatShortDate(dayjs(start).format("YYYY-MM-DD"));
	}
	return `${formatShortDate(dayjs(start).format("YYYY-MM-DD"))} ~ ${formatShortDate(dayjs(end).format("YYYY-MM-DD"))}`;
};

const classifyAutoApplyEntry = (
	start,
	end,
	conversion,
	today,
	actualLeaveUsed = false,
) => {
	if (conversion?.status === "skipped") {
		const startDay = dayjs(start).startOf("day");
		const todayDay = dayjs(today).startOf("day");
		if (startDay.isBefore(todayDay, "day")) {
			return "used";
		}
		return "skipped";
	}
	const startDay = dayjs(start).startOf("day");
	const todayDay = dayjs(today).startOf("day");
	const active =
		conversion?.status === "converted" &&
		conversion?.application_active;
	if (active) {
		if (startDay.isBefore(todayDay, "day")) {
			return "used";
		}
		return "applied";
	}
	if (
		actualLeaveUsed &&
		startDay.isSameOrBefore(todayDay, "day")
	) {
		return "used";
	}
	const deadline = startDay.subtract(4, "day");
	if (
		(todayDay.isAfter(deadline, "day") ||
			todayDay.isSame(deadline, "day")) &&
		startDay.isAfter(todayDay, "day")
	) {
		return "due_soon";
	}
	if (startDay.isSameOrBefore(todayDay, "day")) {
		return "due_soon";
	}
	return "scheduled";
};

const collectPlanEntries = (plan, today) => {
	const entries = [];
	(plan.items || []).forEach((item) => {
		const periods = item.periods?.length
			? item.periods
			: (item.dates || []).map((day) => ({
					start_date: day,
					end_date: day,
					day_count:
						item.leave_type ===
							"오전반차" ||
						item.leave_type === "오후반차"
							? 0.5
							: 1,
				}));
		periods.forEach((period) => {
			const conversion = findConversion(
				item.conversions,
				period.start_date,
				period.end_date,
			);
			const status = classifyAutoApplyEntry(
				period.start_date,
				period.end_date,
				conversion,
				today,
				Boolean(period.actual_leave_used),
			);
			const dayCount =
				period.day_count != null
					? Number(period.day_count)
					: dayCountForPeriod(
							item.leave_type,
							period.start_date,
							period.end_date,
						);
			const autoApplyDate = dayjs(period.start_date)
				.subtract(4, "day")
				.format("YYYY-MM-DD");
			entries.push({
				key: `${item.id}-${period.start_date}-${period.end_date}`,
				itemId: item.id,
				planId: plan.id,
				employeeName: plan.employee_name,
				leaveType: item.leave_type,
				start: period.start_date,
				end: period.end_date,
				dayCount,
				status,
				conversion,
				autoApplyDate,
			});
		});
	});
	return entries.sort((left, right) =>
		left.start.localeCompare(right.start),
	);
};

const resolveReferenceToday = (planRows) => {
	if (!planRows.length) {
		return dayjs().format("YYYY-MM-DD");
	}
	const latest = planRows.reduce((best, plan) => {
		if (!plan.as_of) {
			return best;
		}
		if (!best || plan.as_of > best) {
			return plan.as_of;
		}
		return best;
	}, null);
	return latest || dayjs().format("YYYY-MM-DD");
};

const computeGlobalStats = (planRows, today) => {
	let entryCount = 0;
	let plannedDays = 0;
	let completedEntries = 0;
	let completedDays = 0;
	let dueSoonEntries = 0;
	let remainingEntries = 0;
	let lastUseDate = null;

	planRows.forEach((plan) => {
		const entries = collectPlanEntries(plan, today);
		entryCount += entries.length;
		entries.forEach((entry) => {
			plannedDays += entry.dayCount;
			if (
				entry.status === "used" ||
				entry.status === "applied"
			) {
				completedEntries += 1;
				completedDays += entry.dayCount;
			}
			if (entry.status === "due_soon") {
				dueSoonEntries += 1;
			}
			if (
				entry.status === "scheduled" ||
				entry.status === "due_soon"
			) {
				remainingEntries += 1;
			}
			if (!lastUseDate || entry.start > lastUseDate) {
				lastUseDate = entry.start;
			}
		});
	});

	return {
		planCount: planRows.length,
		plannedDays: Math.round(plannedDays * 10) / 10,
		entryCount,
		completedEntries,
		completedDays: Math.round(completedDays * 10) / 10,
		dueSoonEntries,
		remainingEntries,
		lastUseDate,
	};
};

const planProgress = (entries) => {
	const total = entries.length;
	const done = entries.filter(
		(entry) =>
			entry.status === "used" || entry.status === "applied",
	).length;
	const totalDays = entries.reduce(
		(sum, entry) => sum + entry.dayCount,
		0,
	);
	const doneDays = entries
		.filter(
			(entry) =>
				entry.status === "used" ||
				entry.status === "applied",
		)
		.reduce((sum, entry) => sum + entry.dayCount, 0);
	return { total, done, totalDays, doneDays };
};

const nextAutoApplyForPlan = (entries, today) => {
	const todayDay = dayjs(today).startOf("day");
	const pending = entries.filter((entry) => {
		if (
			entry.status !== "scheduled" &&
			entry.status !== "due_soon"
		) {
			return false;
		}
		return !dayjs(entry.start)
			.startOf("day")
			.isBefore(todayDay, "day");
	});
	if (!pending.length) {
		return null;
	}

	const nextEntry = pending.reduce(
		(best, entry) =>
			!best || entry.start < best.start ? entry : best,
		null,
	);
	const useDay = dayjs(nextEntry.start).startOf("day");
	const daysUntil = useDay.diff(todayDay, "day");
	return {
		entry: nextEntry,
		daysUntil: Math.max(0, daysUntil),
		useDate: nextEntry.start,
	};
};

const groupDatesByMonth = (entries) => {
	const map = new Map();
	entries.forEach((entry) => {
		const monthKey = dayjs(entry.start).format("M");
		if (!map.has(monthKey)) {
			map.set(monthKey, []);
		}
		map.get(monthKey).push(entry);
	});
	return [...map.entries()].sort(
		(left, right) => Number(left[0]) - Number(right[0]),
	);
};

const STATUS_META = {
	used: {
		label: "사용완료",
		chip: "used",
		statusClass: "used",
	},
	applied: {
		label: "자동신청됨",
		chip: "applied",
		statusClass: "applied",
	},
	due_soon: {
		label: "4일 내 신청",
		chip: "due",
		statusClass: "due",
	},
	scheduled: {
		label: "신청예정",
		chip: "scheduled",
		statusClass: "scheduled",
	},
	skipped: {
		label: "개인 직접 신청",
		chip: "skipped",
		statusClass: "skipped",
	},
};

const planMatchesTab = (plan, tab, today) => {
	const entries = collectPlanEntries(plan, today);
	if (tab === "within7") {
		return entries.some((entry) => entry.status === "due_soon");
	}
	if (tab === "complete") {
		return (
			entries.length > 0 &&
			entries.every(
				(entry) =>
					entry.status === "used" ||
					entry.status === "applied" ||
					entry.status === "skipped",
			)
		);
	}
	return true;
};

const formatDueEntryLine = (entry) => {
	const dates =
		entry.start === entry.end
			? formatShortDate(entry.start)
			: `${formatShortDate(entry.start)}~${formatShortDate(entry.end)}`;
	return `${entry.leaveType} ${dates} · ${formatDays(entry.dayCount)}일`;
};

const dueEntryIsAction = (entry) => entry.status === "skipped";

const dueEntryIsApplied = (entry) =>
	entry.status === "applied" || entry.status === "used";

const dueEntryCanConvert = (entry) =>
	entry.status === "due_soon" || entry.status === "scheduled";

const DuePlanMiniCard = ({
	entry,
	converting,
	onConvert,
	onViewApplications,
}) => {
	const meta = STATUS_META[entry.status] || STATUS_META.scheduled;
	const isAction = dueEntryIsAction(entry);
	const isApplied = dueEntryIsApplied(entry);
	const canConvert = dueEntryCanConvert(entry);

	return (
		<div
			className={`leave-usage-plan-due-mini${
				isAction
					? " leave-usage-plan-due-mini--alert"
					: ""
			}${isApplied ? " leave-usage-plan-due-mini--done" : ""}`}>
			<div className="leave-usage-plan-due-mini__text">
				<div className="leave-usage-plan-due-mini__name">
					{entry.employeeName}
				</div>
				<div className="leave-usage-plan-due-mini__detail">
					{formatDueEntryLine(entry)}
				</div>
				{isAction ? (
					<div className="leave-usage-plan-due-mini__warn">
						같은 날 신청 있음 · 변환 불가
					</div>
				) : (
					<span
						className={`leave-usage-plan-due-mini__badge leave-usage-plan-due-mini__badge--${meta.statusClass}`}>
						{isApplied ? "신청됨" : meta.label}
					</span>
				)}
			</div>
			<div className="leave-usage-plan-due-mini__actions">
				{isAction ? (
					<Button
						size="small"
						onClick={() =>
							onViewApplications(entry)
						}>
						신청 내역 확인
					</Button>
				) : canConvert ? (
					<Button
						size="small"
						type="primary"
						ghost
						loading={converting}
						onClick={() => onConvert([entry])}>
						변환
					</Button>
				) : null}
			</div>
		</div>
	);
};

const DuePlanAlertRow = ({ entry, onViewApplications }) => (
	<div className="leave-usage-plan-due-alert">
		<div className="leave-usage-plan-due-alert__main">
			<span className="leave-usage-plan-due-alert__name">
				{entry.employeeName}
			</span>
			<span className="leave-usage-plan-due-alert__detail">
				{formatDueEntryLine(entry)}
			</span>
			<span className="leave-usage-plan-due-alert__warn">
				같은 날 신청 있음 · 변환 불가
			</span>
		</div>
		<Button
			size="small"
			onClick={() => onViewApplications(entry)}>
			신청 내역 확인
		</Button>
	</div>
);

const DateChip = ({ entry }) => {
	const meta = STATUS_META[entry.status] || STATUS_META.scheduled;
	const isRange = entry.end !== entry.start;
	const startDate = dayjs(entry.start);
	const endDate = dayjs(entry.end);
	const tooltip = (
		<>
			사용일 {formatAsOfLabel(entry.start)}
			{isRange ? ` ~ ${formatAsOfLabel(entry.end)}` : ""}
			<br />
			자동신청일 {formatAsOfLabel(entry.autoApplyDate)} (D-4)
		</>
	);
	const primaryLabel = isRange
		? `${startDate.format("D")}-${endDate.format("D")}`
		: startDate.format("D");
	const secondaryLabel = isRange
		? `${formatDays(entry.dayCount)}일`
		: WEEKDAY_KO[startDate.day()];

	return (
		<Tooltip title={tooltip}>
			<span
				className={`leave-usage-plan-date-chip leave-usage-plan-date-chip--${meta.chip}${
					isRange
						? " leave-usage-plan-date-chip--range"
						: ""
				}`}>
				<span className="leave-usage-plan-date-chip__day">
					{primaryLabel}
				</span>
				<span>{secondaryLabel}</span>
			</span>
		</Tooltip>
	);
};

const planMatchesKeyword = (plan, keyword) => {
	const parts = [
		plan.employee_name,
		plan.classification_name,
		plan.department_name,
		plan.job_title,
		plan.period_summary,
	];
	(plan.items || []).forEach((item) => {
		parts.push(item.leave_type, item.memo);
		(item.dates || []).forEach((day) => parts.push(day));
	});
	return parts.some((value) =>
		String(value || "")
			.toLowerCase()
			.includes(keyword),
	);
};

const LeaveUsagePlanManage = () => {
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const [loading, setLoading] = useState(false);
	const [plans, setPlans] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [activeTab, setActiveTab] = useState("all");
	const [employees, setEmployees] = useState([]);
	const [expandedKeys, setExpandedKeys] = useState([]);
	const [editorOpen, setEditorOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [preview, setPreview] = useState(null);
	const [saving, setSaving] = useState(false);
	const [converting, setConverting] = useState(false);
	const [dueAppliedExpanded, setDueAppliedExpanded] =
		useState(false);
	const [form] = Form.useForm();
	const watchedItems = Form.useWatch("items", form);
	const watchedCarryover = Form.useWatch("carryover_days", form);

	const loadPlans = useCallback(async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/leave/usage-plans/",
			);
			setPlans(response.data || []);
		} catch {
			message.error("연차휴가 계획서를 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, []);

	const loadEmployees = useCallback(async () => {
		try {
			const response = await useAxios.get(
				"/users/employee-info/",
			);
			const rows = (response.data || []).filter(
				(row) => row.employment_status === "근무 중",
			);
			setEmployees(rows);
		} catch {
			setEmployees([]);
		}
	}, []);

	useEffect(() => {
		loadPlans();
		loadEmployees();
	}, [loadPlans, loadEmployees]);

	const referenceToday = useMemo(
		() => resolveReferenceToday(plans),
		[plans],
	);

	const globalStats = useMemo(
		() => computeGlobalStats(plans, referenceToday),
		[plans, referenceToday],
	);

	const tabCounts = useMemo(() => {
		const within7 = plans.filter((plan) =>
			planMatchesTab(plan, "within7", referenceToday),
		).length;
		const complete = plans.filter((plan) =>
			planMatchesTab(plan, "complete", referenceToday),
		).length;
		return {
			all: plans.length,
			within7,
			complete,
		};
	}, [plans, referenceToday]);

	const filteredPlans = useMemo(() => {
		const keyword = searchText.trim().toLowerCase();
		return plans.filter((plan) => {
			if (!planMatchesTab(plan, activeTab, referenceToday)) {
				return false;
			}
			if (!keyword) {
				return true;
			}
			return planMatchesKeyword(plan, keyword);
		});
	}, [plans, searchText, activeTab, referenceToday]);

	const dueSoonConvertEntries = useMemo(() => {
		const todayDay = dayjs(referenceToday).startOf("day");
		const entries = [];
		plans.forEach((plan) => {
			collectPlanEntries(plan, referenceToday).forEach(
				(entry) => {
					const startDay = dayjs(
						entry.start,
					).startOf("day");
					const daysUntil = startDay.diff(
						todayDay,
						"day",
					);
					if (daysUntil < 1 || daysUntil > 4) {
						return;
					}
					entries.push(entry);
				},
			);
		});
		return entries.sort((left, right) =>
			left.start.localeCompare(right.start),
		);
	}, [plans, referenceToday]);

	const convertibleDueSoonEntries = useMemo(
		() =>
			dueSoonConvertEntries.filter((entry) =>
				dueEntryCanConvert(entry),
			),
		[dueSoonConvertEntries],
	);

	const dueSoonBuckets = useMemo(() => {
		const action = [];
		const applied = [];
		const convert = [];
		dueSoonConvertEntries.forEach((entry) => {
			if (dueEntryIsAction(entry)) {
				action.push(entry);
			} else if (dueEntryIsApplied(entry)) {
				applied.push(entry);
			} else if (dueEntryCanConvert(entry)) {
				convert.push(entry);
			}
		});
		return {
			action,
			applied,
			convert,
			total: dueSoonConvertEntries.length,
		};
	}, [dueSoonConvertEntries]);

	const dueSoonGridEntries = useMemo(() => {
		if (dueAppliedExpanded) {
			return dueSoonConvertEntries;
		}
		return dueSoonConvertEntries.filter(
			(entry) =>
				!dueEntryIsApplied(entry) &&
				!dueEntryIsAction(entry),
		);
	}, [dueSoonConvertEntries, dueAppliedExpanded]);

	const dueAppliedNamesPreview = useMemo(() => {
		const names = [
			...new Set(
				dueSoonBuckets.applied.map(
					(entry) => entry.employeeName,
				),
			),
		];
		return names.join(" · ");
	}, [dueSoonBuckets.applied]);

	const openLeaveApplications = useCallback(
		(entry) => {
			navigate("/utils/leave/view-applications", {
				state: {
					nameSearch: entry.employeeName,
				},
			});
		},
		[navigate],
	);

	const plannedDays = useMemo(
		() =>
			(watchedItems || []).reduce(
				(sum, item) => sum + itemDayCount(item),
				0,
			),
		[watchedItems],
	);

	const carryoverDays = Number(watchedCarryover);
	const safeCarryover =
		Number.isFinite(carryoverDays) && carryoverDays > 0
			? carryoverDays
			: 0;
	const carryoverLimit = preview
		? Number(
				preview.carryover_limit != null
					? preview.carryover_limit
					: preview.remaining_days,
			)
		: 0;
	const planTargetDays = preview
		? Math.max(
				0,
				Math.round(
					(Number(preview.remaining_days) -
						safeCarryover) *
						10,
				) / 10,
			)
		: 0;

	const openCreate = () => {
		setEditing(null);
		setPreview(null);
		form.resetFields();
		form.setFieldsValue({
			carryover_days: 0,
			items: [{ leave_type: "연차", dates: [], memo: "" }],
		});
		setEditorOpen(true);
	};

	const openEdit = (plan) => {
		setEditing(plan);
		setPreview({
			department_name: plan.department_name,
			classification_name: plan.classification_name,
			job_title: plan.job_title,
			employee_name: plan.employee_name,
			hire_date: plan.hire_date,
			period_start: plan.period_start,
			period_end: plan.period_end,
			granted_days: plan.granted_days,
			used_days: plan.used_days,
			remaining_days: plan.remaining_days,
			carryover_days: plan.carryover_days,
			target_days: plan.target_days,
			as_of: plan.as_of,
		});
		form.setFieldsValue({
			user_id: plan.user_id,
			carryover_days: Number(plan.carryover_days) || 0,
			items: expandPlanItemsForForm(plan.items || []),
		});
		setEditorOpen(true);
	};

	const selectEmployee = async (userId) => {
		setPreview(null);
		if (!editing) {
			form.setFieldValue("carryover_days", 0);
		}
		if (!userId) {
			return;
		}
		try {
			const response = await useAxios.get(
				"/utils/leave/usage-plans/preview/",
				{ params: { user_id: userId } },
			);
			setPreview(response.data);
		} catch (error) {
			message.error(
				error?.response?.data?.error ||
					"직원 연차 현황을 불러오지 못했습니다.",
			);
		}
	};

	const submit = async () => {
		try {
			const values = await form.validateFields();
			if (!preview) {
				message.error("직원을 선택하세요.");
				return;
			}
			const items = (values.items || []).map((item) => ({
				id: item.id,
				leave_type: item.leave_type,
				dates: (item.dates || []).map((day) =>
					dayjs(day).format("YYYY-MM-DD"),
				),
				memo: item.memo || "",
			}));
			const seenDates = new Set();
			for (const item of items) {
				for (const day of item.dates) {
					if (seenDates.has(day)) {
						message.error(
							`${day} 날짜가 중복되었습니다.`,
						);
						return;
					}
					seenDates.add(day);
				}
			}
			const carryover = Number(values.carryover_days) || 0;
			if (carryover > carryoverLimit + 1e-9) {
				message.error(
					"내년 이월 연차는 잔여 연차를 넘을 수 없습니다.",
				);
				return;
			}
			setSaving(true);
			const payload = {
				user_id: values.user_id,
				items,
				carryover_days: carryover,
			};
			if (editing) {
				await useAxios.put(
					`/utils/leave/usage-plans/${editing.id}/`,
					payload,
				);
				message.success("계획서를 수정했습니다.");
			} else {
				await useAxios.post(
					"/utils/leave/usage-plans/",
					payload,
				);
				message.success("계획서를 저장했습니다.");
			}
			setEditorOpen(false);
			loadPlans();
		} catch (error) {
			if (error?.errorFields) {
				return;
			}
			message.error(
				error?.response?.data?.error ||
					"계획서 저장에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const removePlan = (plan) => {
		Modal.confirm({
			title: "계획서를 삭제할까요?",
			content: `${plan.employee_name} 계획서를 삭제합니다.`,
			okText: "삭제",
			okButtonProps: { danger: true },
			cancelText: "닫기",
			onOk: async () => {
				try {
					await useAxios.delete(
						`/utils/leave/usage-plans/${plan.id}/`,
					);
					message.success(
						"계획서를 삭제했습니다.",
					);
					loadPlans();
				} catch (error) {
					message.error(
						error?.response?.data?.error ||
							"계획서 삭제에 실패했습니다.",
					);
				}
			},
		});
	};

	const convertPlanEntries = (entries) => {
		const targets = entries.map((entry) => ({
			item_id: entry.itemId,
			start_date: entry.start,
			end_date: entry.end,
		}));
		if (!targets.length) {
			message.warning("변환할 계획이 없습니다.");
			return;
		}
		Modal.confirm({
			title: "연차 신청으로 변환할까요?",
			content: `${targets.length}건을 변환합니다. 신청일은 사용예정일로 저장됩니다.`,
			okText: "변환",
			cancelText: "닫기",
			onOk: async () => {
				try {
					setConverting(true);
					const response = await useAxios.post(
						"/utils/leave/usage-plans/convert/",
						{ targets },
					);
					const summary =
						response.data?.summary || {};
					const parts = [];
					if (summary.converted) {
						parts.push(
							`변환 ${summary.converted}건`,
						);
					}
					if (summary.already) {
						parts.push(
							`이미 신청 ${summary.already}건`,
						);
					}
					if (summary.skipped) {
						parts.push(
							`개인 직접 신청 ${summary.skipped}건`,
						);
					}
					if (summary.insufficient) {
						parts.push(
							`잔여 부족 ${summary.insufficient}건`,
						);
					}
					message.success(
						parts.length
							? parts.join(", ")
							: "변환을 완료했습니다.",
					);
					await loadPlans();
				} catch (error) {
					message.error(
						error?.response?.data?.error ||
							"연차 신청 변환에 실패했습니다.",
					);
				} finally {
					setConverting(false);
				}
			},
		});
	};

	const columns = [
		{
			title: "이름",
			dataIndex: "employee_name",
			width: 88,
		},
		{
			title: "직분",
			dataIndex: "classification_name",
			width: 88,
			render: (value) => value || "-",
		},
		{
			title: "소속",
			dataIndex: "department_name",
			width: 100,
			render: (value) => value || "-",
		},
		{
			title: "사용 예정일",
			key: "scheduled_dates",
			width: 220,
			render: (_, plan) => {
				const entries = collectPlanEntries(
					plan,
					referenceToday,
				);
				if (!entries.length) {
					return "-";
				}
				return (
					<div className="leave-usage-plan-dates">
						{groupDatesByMonth(entries).map(
							([
								month,
								monthEntries,
							]) => (
								<div
									key={
										month
									}
									className="leave-usage-plan-dates__month">
									<span className="leave-usage-plan-dates__month-label">
										{
											month
										}

										월
									</span>
									<div className="leave-usage-plan-dates__chips">
										{monthEntries.map(
											(
												entry,
											) => (
												<DateChip
													key={
														entry.key
													}
													entry={
														entry
													}
												/>
											),
										)}
									</div>
								</div>
							),
						)}
					</div>
				);
			},
		},
		{
			title: "계획",
			key: "plan_days",
			width: 64,
			align: "center",
			render: (_, plan) => {
				const days = collectPlanEntries(
					plan,
					referenceToday,
				).reduce(
					(sum, entry) => sum + entry.dayCount,
					0,
				);
				return `${formatDays(days)}일`;
			},
		},
		{
			title: "자동신청 진행",
			key: "progress",
			width: 140,
			render: (_, plan) => {
				const entries = collectPlanEntries(
					plan,
					referenceToday,
				);
				const progress = planProgress(entries);
				if (!progress.total) {
					return "-";
				}
				const percent = Math.round(
					(progress.done / progress.total) * 100,
				);
				return (
					<div className="leave-usage-plan-progress">
						<div className="leave-usage-plan-progress__text">
							{progress.done} /{" "}
							{progress.total}건
						</div>
						<Progress
							percent={percent}
							showInfo={false}
							size="small"
							strokeColor="#3b82f6"
						/>
						<div className="leave-usage-plan-progress__sub">
							{formatDays(
								progress.doneDays,
							)}
							일 /{" "}
							{formatDays(
								progress.totalDays,
							)}
							일 반영
						</div>
					</div>
				);
			},
		},
		{
			title: "다음 자동신청",
			key: "next_auto",
			width: 108,
			render: (_, plan) => {
				const entries = collectPlanEntries(
					plan,
					referenceToday,
				);
				const next = nextAutoApplyForPlan(
					entries,
					referenceToday,
				);
				if (!next) {
					return (
						<span className="leave-usage-plan-next__empty">
							-
						</span>
					);
				}
				return (
					<div className="leave-usage-plan-next">
						<span className="leave-usage-plan-next__d">
							D-
							{next.daysUntil}
						</span>
						<span className="leave-usage-plan-next__date">
							{formatShortDate(
								next.useDate,
							)}
						</span>
					</div>
				);
			},
		},
		{
			title: "내년 이월",
			dataIndex: "carryover_days",
			width: 80,
			align: "center",
			render: (value) => `${formatDays(value)}일`,
		},
		{
			title: "작업",
			key: "actions",
			width: 120,
			render: (_, plan) => (
				<Space
					className="leave-usage-plan-actions"
					onClick={(event) =>
						event.stopPropagation()
					}>
					<Button
						size="small"
						onClick={() => openEdit(plan)}>
						수정
					</Button>
					<Button
						size="small"
						danger
						onClick={() =>
							removePlan(plan)
						}>
						삭제
					</Button>
				</Space>
			),
		},
	];

	return (
		<>
			<PageTitle title="연차 사용계획 관리" />
			<div className="page leave-usage-plan-page">
				<PageHeader
					title="연차 사용계획 관리"
					subtitle={`기준일 ${formatAsOfLabel(
						referenceToday,
					)} · 사용일 4일 전(D-4)에 순차 자동신청`}
					actions={
						<Button
							type="primary"
							onClick={openCreate}>
							계획서 입력
						</Button>
					}
				/>

				<div className="leave-usage-plan-page__stats">
					<div className="leave-usage-plan-stat">
						<div className="leave-usage-plan-stat__label">
							계획서 제출
						</div>
						<div className="leave-usage-plan-stat__value">
							{globalStats.planCount}
							명
						</div>
						<div className="leave-usage-plan-stat__hint">
							계획{" "}
							{formatDays(
								globalStats.plannedDays,
							)}
							일 ·{" "}
							{globalStats.entryCount}
							건
						</div>
					</div>
					<div className="leave-usage-plan-stat">
						<div className="leave-usage-plan-stat__label">
							자동신청 완료
						</div>
						<div className="leave-usage-plan-stat__value leave-usage-plan-stat__value--blue">
							{
								globalStats.completedEntries
							}
							건
						</div>
						<div className="leave-usage-plan-stat__hint">
							{formatDays(
								globalStats.completedDays,
							)}
							일 반영 (사용완료 포함)
						</div>
					</div>
					<div className="leave-usage-plan-stat">
						<div className="leave-usage-plan-stat__label">
							4일 내 자동신청
						</div>
						<div className="leave-usage-plan-stat__value leave-usage-plan-stat__value--orange">
							{
								globalStats.dueSoonEntries
							}
							건
						</div>
						<div className="leave-usage-plan-stat__hint">
							기준일 이후 4일 안 순차 신청
						</div>
					</div>
					<div className="leave-usage-plan-stat">
						<div className="leave-usage-plan-stat__label">
							남은 신청 예정
						</div>
						<div className="leave-usage-plan-stat__value leave-usage-plan-stat__value--green">
							{
								globalStats.remainingEntries
							}
							건
						</div>
						<div className="leave-usage-plan-stat__hint">
							{globalStats.lastUseDate
								? `${formatShortDate(globalStats.lastUseDate)} 사용분까지`
								: "예정 없음"}
						</div>
					</div>
				</div>

				<div className="leave-usage-plan-due-card">
					<div className="leave-usage-plan-due-card__header">
						<div className="leave-usage-plan-due-card__heading">
							<div className="leave-usage-plan-due-card__title">
								사용예정일 4일 이내 계획
							</div>
							<div className="leave-usage-plan-due-card__badges">
								<span className="leave-usage-plan-due-badge">
									총 {dueSoonBuckets.total}
								</span>
								{dueSoonBuckets.action.length ? (
									<span className="leave-usage-plan-due-badge leave-usage-plan-due-badge--danger">
										조치 필요{" "}
										{
											dueSoonBuckets
												.action.length
										}
									</span>
								) : null}
								{dueSoonBuckets.applied.length ? (
									<span className="leave-usage-plan-due-badge leave-usage-plan-due-badge--info">
										이미 신청됨{" "}
										{
											dueSoonBuckets
												.applied.length
										}
									</span>
								) : null}
							</div>
						</div>
						{convertibleDueSoonEntries.length ? (
							<Button
								type="primary"
								size="small"
								loading={converting}
								onClick={() =>
									convertPlanEntries(
										convertibleDueSoonEntries,
									)
								}>
								변환 가능{" "}
								{
									convertibleDueSoonEntries.length
								}
								건 일괄 변환
							</Button>
						) : null}
					</div>
					<div className="leave-usage-plan-due-card__hint">
						사용일 4일 전인 날짜는 순차적으로 자동
						연차 신청으로 변환됩니다. 같은 날
						신청이 있으면 건너뜁니다.
					</div>
					{dueSoonConvertEntries.length ? (
						<>
							{!dueAppliedExpanded &&
							dueSoonBuckets.action.length
								? dueSoonBuckets.action.map(
										(entry) => (
											<DuePlanAlertRow
												key={
													entry.key
												}
												entry={
													entry
												}
												onViewApplications={
													openLeaveApplications
												}
											/>
										),
									)
								: null}
							{dueSoonGridEntries.length ? (
								<div
									className={`leave-usage-plan-due-grid${
										dueAppliedExpanded
											? " leave-usage-plan-due-grid--expanded"
											: ""
									}`}>
									{dueSoonGridEntries.map(
										(entry) => (
											<DuePlanMiniCard
												key={
													entry.key
												}
												entry={
													entry
												}
												converting={
													converting
												}
												onConvert={
													convertPlanEntries
												}
												onViewApplications={
													openLeaveApplications
												}
											/>
										),
									)}
								</div>
							) : null}
							{dueSoonBuckets.applied.length ? (
								<div className="leave-usage-plan-due-card__footer">
									{!dueAppliedExpanded ? (
										<>
											<span className="leave-usage-plan-due-card__footer-text">
												이미 신청된{" "}
												{
													dueSoonBuckets
														.applied
														.length
												}
												건은 접어
												두었습니다
												{dueAppliedNamesPreview
													? ` · ${dueAppliedNamesPreview}`
													: ""}
											</span>
											<Button
												type="link"
												size="small"
												className="leave-usage-plan-due-card__toggle"
												onClick={() =>
													setDueAppliedExpanded(
														true,
													)
												}>
												신청된{" "}
												{
													dueSoonBuckets
														.applied
														.length
												}
												건 펼치기
											</Button>
										</>
									) : (
										<Button
											type="link"
											size="small"
											className="leave-usage-plan-due-card__toggle"
											onClick={() =>
												setDueAppliedExpanded(
													false,
												)
											}>
											신청된{" "}
											{
												dueSoonBuckets
													.applied
													.length
											}
											건 접기
										</Button>
									)}
								</div>
							) : null}
						</>
					) : (
						<div className="leave-usage-plan-due-card__empty">
							4일 이내에 사용할 계획이 없습니다.
						</div>
					)}
				</div>

				<div className="surface-card leave-usage-plan-panel">
					<div className="leave-usage-plan-toolbar">
						<div className="leave-usage-plan-toolbar__left">
							<Input
								allowClear
								className="leave-usage-plan-toolbar__search"
								placeholder="이름, 소속 검색"
								value={
									searchText
								}
								onChange={(
									event,
								) =>
									setSearchText(
										event
											.target
											.value,
									)
								}
							/>
							<div className="leave-usage-plan-tabs">
								<button
									type="button"
									className={`leave-usage-plan-tab${
										activeTab ===
										"all"
											? " is-active"
											: ""
									}`}
									onClick={() =>
										setActiveTab(
											"all",
										)
									}>
									전체{" "}
									{
										tabCounts.all
									}
								</button>
								<button
									type="button"
									className={`leave-usage-plan-tab${
										activeTab ===
										"within7"
											? " is-active"
											: ""
									}`}
									onClick={() =>
										setActiveTab(
											"within7",
										)
									}>
									4일 내
									자동신청{" "}
									{
										tabCounts.within7
									}
								</button>
								<button
									type="button"
									className={`leave-usage-plan-tab${
										activeTab ===
										"complete"
											? " is-active"
											: ""
									}`}
									onClick={() =>
										setActiveTab(
											"complete",
										)
									}>
									신청
									완료{" "}
									{
										tabCounts.complete
									}
								</button>
							</div>
						</div>
						<div className="leave-usage-plan-legend">
							<span className="leave-usage-plan-legend__item">
								<span className="leave-usage-plan-legend__dot leave-usage-plan-legend__dot--used" />
								사용완료
							</span>
							<span className="leave-usage-plan-legend__item">
								<span className="leave-usage-plan-legend__dot leave-usage-plan-legend__dot--applied" />
								자동신청됨
							</span>
							<span className="leave-usage-plan-legend__item">
								<span className="leave-usage-plan-legend__dot leave-usage-plan-legend__dot--due" />
								4일 내 신청
							</span>
							<span className="leave-usage-plan-legend__item">
								<span className="leave-usage-plan-legend__dot leave-usage-plan-legend__dot--scheduled" />
								신청예정
							</span>
							<span className="leave-usage-plan-legend__item">
								<span className="leave-usage-plan-legend__dot leave-usage-plan-legend__dot--skipped" />
								개인 직접 신청
							</span>
						</div>
					</div>

					<Table
						className="leave-usage-plan-table"
						rowKey="id"
						loading={loading}
						columns={columns}
						dataSource={filteredPlans}
						scroll={{ x: 1100 }}
						sticky={{ offsetHeader: 64 }}
						locale={{
							emptyText: searchText.trim()
								? "검색 결과가 없습니다."
								: "데이터가 없습니다.",
						}}
						pagination={false}
						expandable={{
							expandedRowKeys:
								expandedKeys,
							onExpand: (
								expanded,
								plan,
							) => {
								setExpandedKeys(
									(
										prev,
									) =>
										expanded
											? [
													...prev,
													plan.id,
												]
											: prev.filter(
													(
														id,
													) =>
														id !==
														plan.id,
												),
								);
							},
							expandedRowRender: (
								plan,
							) => {
								const entries =
									collectPlanEntries(
										plan,
										referenceToday,
									);
								return (
									<div className="leave-usage-plan-expand">
										<div className="leave-usage-plan-expand__title">
											자동신청
											내역
											·{" "}
											{
												plan.employee_name
											}
										</div>
										<Table
											className="leave-usage-plan-expand__table"
											size="small"
											pagination={
												false
											}
											rowKey="key"
											dataSource={
												entries
											}
											columns={[
												{
													title: "사용일",
													dataIndex: "start",
													width: 140,
													render: (
														_,
														row,
													) =>
														row.start ===
														row.end
															? formatAsOfLabel(
																	row.start,
																)
															: `${formatAsOfLabel(row.start)} ~ ${formatShortDate(row.end)}`,
												},
												{
													title: "일수",
													dataIndex: "dayCount",
													width: 72,
													render: (
														value,
													) =>
														`${formatDays(value)}일`,
												},
												{
													title: "자동신청일 (D-4)",
													dataIndex: "autoApplyDate",
													width: 160,
													render: (
														value,
													) =>
														formatAsOfLabel(
															value,
														),
												},
												{
													title: "상태",
													key: "status",
													width: 120,
													render: (
														_,
														row,
													) => {
														const meta =
															STATUS_META[
																row
																	.status
															] ||
															STATUS_META.scheduled;
														return (
															<span
																className={`leave-usage-plan-status leave-usage-plan-status--${meta.statusClass}`}>
																{
																	meta.label
																}
															</span>
														);
													},
												},
												{
													title: "",
													key: "hold",
													width: 100,
													render: (
														_,
														row,
													) =>
														row.status ===
														"scheduled" ? (
															<Button
																size="small"
																type="link"
																disabled>
																신청
																보류
															</Button>
														) : null,
												},
											]}
										/>
									</div>
								);
							},
						}}
						onRow={(plan) => ({
							onClick: () => {
								setExpandedKeys(
									(
										prev,
									) =>
										prev.includes(
											plan.id,
										)
											? prev.filter(
													(
														id,
													) =>
														id !==
														plan.id,
												)
											: [
													...prev,
													plan.id,
												],
								);
							},
							style: {
								cursor: "pointer",
							},
						})}
					/>

					<div className="leave-usage-plan-footer">
						<span>
							{filteredPlans.length}건
							표시
						</span>
						<span className="leave-usage-plan-footer__hint">
							칩에 마우스를 올리면
							전체 날짜와 자동신청일이
							보입니다
						</span>
					</div>
				</div>
			</div>
			<Modal
				title={editing ? "계획서 수정" : "계획서 입력"}
				open={editorOpen}
				onCancel={() => setEditorOpen(false)}
				onOk={submit}
				okText="저장"
				cancelText="닫기"
				confirmLoading={saving}
				width={isMobile ? "100%" : 720}
				destroyOnClose>
				<Form form={form} layout="vertical">
					<Form.Item
						name="user_id"
						label="직원"
						rules={[
							{
								required: true,
								message: "직원을 선택하세요",
							},
						]}>
						<Select
							showSearch
							optionFilterProp="label"
							placeholder="직원을 선택하세요"
							disabled={Boolean(
								editing,
							)}
							onChange={
								selectEmployee
							}
							options={employees.map(
								(row) => ({
									value: row.user_id,
									label: `${row.user_name} (${row.employee_team_name || "소속 없음"})`,
								}),
							)}
						/>
					</Form.Item>
					{preview ? (
						<div className="ant-descriptions ant-descriptions-small ant-descriptions-bordered leave-plan-preview">
							<div className="ant-descriptions-view">
								<table
									style={{
										tableLayout:
											"fixed",
									}}>
									<colgroup>
										<col
											style={{
												width: "18%",
											}}
										/>
										<col
											style={{
												width: "32%",
											}}
										/>
										<col
											style={{
												width: "22%",
											}}
										/>
										<col
											style={{
												width: "28%",
											}}
										/>
									</colgroup>
									<tbody>
										<tr className="ant-descriptions-row">
											<th className="ant-descriptions-item-label">
												소속
											</th>
											<td className="ant-descriptions-item-content">
												{preview.department_name ||
													"-"}
											</td>
											<th className="ant-descriptions-item-label">
												직분
											</th>
											<td className="ant-descriptions-item-content">
												{preview.classification_name ||
													"-"}
											</td>
										</tr>
										<tr className="ant-descriptions-row">
											<th className="ant-descriptions-item-label">
												직위
											</th>
											<td className="ant-descriptions-item-content">
												{preview.job_title ||
													"-"}
											</td>
											<th className="ant-descriptions-item-label">
												성명
											</th>
											<td className="ant-descriptions-item-content">
												{
													preview.employee_name
												}
											</td>
										</tr>
										<tr className="ant-descriptions-row">
											<th className="ant-descriptions-item-label">
												입사일
											</th>
											<td className="ant-descriptions-item-content">
												{
													preview.hire_date
												}
											</td>
											<th className="ant-descriptions-item-label">
												기준일
											</th>
											<td className="ant-descriptions-item-content">
												{
													preview.as_of
												}
											</td>
										</tr>
										<tr className="ant-descriptions-row">
											<th className="ant-descriptions-item-label">
												연차
												산정기간
											</th>
											<td
												className="ant-descriptions-item-content"
												colSpan={
													3
												}>
												{
													preview.period_start
												}{" "}
												~{" "}
												{
													preview.period_end
												}
											</td>
										</tr>
										<tr className="ant-descriptions-row">
											<th className="ant-descriptions-item-label">
												발생
												연차
											</th>
											<td className="ant-descriptions-item-content">
												{formatDays(
													preview.granted_days,
												)}

												일
											</td>
											<th className="ant-descriptions-item-label">
												사용
												연차
											</th>
											<td className="ant-descriptions-item-content">
												{formatDays(
													preview.used_days,
												)}

												일
											</td>
										</tr>
										<tr className="ant-descriptions-row">
											<th
												className="ant-descriptions-item-label"
												rowSpan={
													2
												}>
												미사용(잔여)
												연차
											</th>
											<td
												className="ant-descriptions-item-content"
												rowSpan={
													2
												}>
												{formatDays(
													preview.remaining_days,
												)}

												일
											</td>
											<th className="ant-descriptions-item-label">
												사용계획
												대상
												일수
											</th>
											<td className="ant-descriptions-item-content">
												{formatDays(
													plannedDays,
												)}

												/
												{formatDays(
													planTargetDays,
												)}
											</td>
										</tr>
										<tr className="ant-descriptions-row">
											<th className="ant-descriptions-item-label">
												내년
												이월
												연차
											</th>
											<td className="ant-descriptions-item-content">
												<Form.Item
													name="carryover_days"
													style={{
														marginBottom: 0,
													}}
													rules={[
														{
															validator: (
																_,
																value,
															) => {
																if (
																	value ==
																		null ||
																	value ===
																		""
																) {
																	return Promise.resolve();
																}
																const number =
																	Number(
																		value,
																	);
																if (
																	!Number.isFinite(
																		number,
																	) ||
																	number <
																		0
																) {
																	return Promise.reject(
																		new Error(
																			"0 이상의 일수를 입력하세요",
																		),
																	);
																}
																if (
																	Math.abs(
																		number *
																			2 -
																			Math.round(
																				number *
																					2,
																			),
																	) >
																	1e-9
																) {
																	return Promise.reject(
																		new Error(
																			"0.5 단위로 입력하세요",
																		),
																	);
																}
																if (
																	number >
																	carryoverLimit +
																		1e-9
																) {
																	return Promise.reject(
																		new Error(
																			"잔여 연차를 넘을 수 없습니다",
																		),
																	);
																}
																return Promise.resolve();
															},
														},
													]}>
													<InputNumber
														min={
															0
														}
														max={
															carryoverLimit ||
															0
														}
														step={
															0.5
														}
														precision={
															1
														}
														size="small"
														style={{
															width: "100%",
														}}
														addonAfter="일"
													/>
												</Form.Item>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<Text type="secondary">
							직원을 선택하면
							인적사항과 잔여 연차가
							채워집니다.
						</Text>
					)}
					{preview &&
					plannedDays > planTargetDays ? (
						<Text
							type="warning"
							style={{
								display: "block",
								marginTop: 12,
							}}>
							계획 일수(
							{formatDays(
								plannedDays,
							)}
							일)가 사용계획 대상
							일수(
							{formatDays(
								planTargetDays,
							)}
							일)보다 많습니다.
						</Text>
					) : null}
					<Form.List name="items">
						{(fields, { add, remove }) => (
							<div
								style={{
									marginTop: 16,
								}}>
								{fields.map(
									(
										field,
										index,
									) => {
										const locked =
											form.getFieldValue(
												[
													"items",
													field.name,
													"locked",
												],
											);
										const periodDates =
											form.getFieldValue(
												[
													"items",
													field.name,
													"dates",
												],
											) || [];
										const periodLabel =
											formatPeriodTitle(
												periodDates,
											);
										return (
											<Card
												key={
													field.key
												}
												size="small"
												title={
													periodLabel
														? `${index + 1}번 · ${periodLabel}`
														: `${index + 1}번`
												}
												style={{
													marginBottom: 12,
												}}
												extra={
													fields.length >
														1 &&
													!locked ? (
														<Button
															type="text"
															danger
															icon={
																<MinusCircleOutlined />
															}
															onClick={() =>
																remove(
																	field.name,
																)
															}
														/>
													) : null
												}>
												<Form.Item
													name={[
														field.name,
														"id",
													]}
													hidden>
													<Input />
												</Form.Item>
												<Form.Item
													name={[
														field.name,
														"locked",
													]}
													hidden>
													<Input />
												</Form.Item>
												<Form.Item
													name={[
														field.name,
														"leave_type",
													]}
													label="구분"
													rules={[
														{
															required: true,
															message: "구분을 선택하세요",
														},
													]}>
													<Select
														options={
															LEAVE_TYPE_OPTIONS
														}
														disabled={
															locked
														}
													/>
												</Form.Item>
												<Form.Item
													name={[
														field.name,
														"dates",
													]}
													label="사용 예정일"
													rules={[
														{
															required: true,
															type: "array",
															min: 1,
															message: "사용일을 하나 이상 선택하세요",
														},
													]}>
													<LeaveMultiDatePicker
														disabledDate={
															disableSunday
														}
														placeholder="사용일을 선택하세요"
														disabled={
															locked
														}
													/>
												</Form.Item>
												<Form.Item
													name={[
														field.name,
														"memo",
													]}
													label="비고">
													<TextArea
														rows={
															2
														}
														disabled={
															locked
														}
													/>
												</Form.Item>
												{locked ? (
													<Text type="secondary">
														이
														날짜
														구간은
														이미
														연차
														신청으로
														전환되어
														수정할
														수
														없습니다.
														다른
														구간은
														그대로
														편집할
														수
														있습니다.
													</Text>
												) : null}
											</Card>
										);
									},
								)}
								<Button
									type="dashed"
									block
									icon={
										<PlusOutlined />
									}
									onClick={() =>
										add(
											{
												leave_type: "연차",
												dates: [],
												memo: "",
											},
										)
									}>
									계획 행
									추가
								</Button>
							</div>
						)}
					</Form.List>
				</Form>
			</Modal>
		</>
	);
};

export default LeaveUsagePlanManage;
